// @flow
import React, { Component } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Modal from '../../../../../elements/Modal/Modal';
import { FormValidator } from '../../../../../utils/form/index';
import { searchAdmin1Api, searchAdmin2Api, searchAdmin3Api, endpoints } from '../../api';
import AddressForm from '../../../../../elements/AddressForm/AddressForm';
import Cleave from 'cleave.js/dist/cleave-react';
import { getAddressFormValidator } from '../../../../../utils/address';

import commonStyle from '../../../PreferencesViewCommon.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';

import type { FormValidationResult } from '../../../../../utils/form/index';
import type { Country, Admin } from '../../../../../entities/index';
import type { Option } from '../../../../../elements/Select/Select';
import type { Address } from '../../../../../entities';
import type { AddressRequest } from '../../api';
import type { LocalValuesState } from '../../../../local-values/duck';

type Props = {
  +handleClose: () => void;
  +postAddress: (address: AddressRequest) => void;
  +editAddress: (address: AddressRequest, addressId: string) => void;
  +address?: Address;
  +isPutting?: boolean;
  +isPosting?: boolean;
  +addressModalVisible: boolean;
  +cleanModalState?: boolean;
  +localValues?: LocalValuesState;
};

type State = {
  country: {
    value: Country;
    errors?: string[];
  };
  admin1: {
    label?: string;
    value: string;
    errors?: string[];
  };
  admin1Items: Admin[];
  isLoadingAdmin1?: boolean;
  admin2: {
    label?: string;
    value: string;
    errors?: string[];
  };
  admin2Items: Admin[];
  isLoadingAdmin2?: boolean;
  admin3: {
    label?: string;
    value: string;
    errors?: string[];
  };
  admin3Items: Admin[];
  isLoadingAdmin3?: boolean;
  address1: {
    value: string;
    errors?: string[];
  };
  address2: {
    value: string;
  };
  firstName: {
    value: string;
    errors?: string[];
  };
  idNumber: {
    value: string;
    errors?: string[];
  };
  lastName: {
    value: string;
    errors?: string[];
  };
  name: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  zipCode: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  title: {
    add: {
      id: 'preferences.savedAddresses.newAddress.title',
    },
    edit: {
      id: 'preferences.savedAddresses.editAddress.title',
    }
  },
  button: {
    cancel: {
      id: 'preferences.savedAddresses.button.cancel',
    },
    add: {
      id: 'preferences.savedAddresses.button.add',
    },
    edit: {
      id: 'preferences.savedAddresses.button.edit',
    },
  }
});

const initialState = {
  country: { value: '', },
  admin1: { value: '', },
  admin2: { value: '', },
  admin3: { value: '', },
  admin1Items: [],
  admin2Items: [],
  admin3Items: [],
  isLoadingAdmin1: false,
  isLoadingAdmin2: false,
  isLoadingAdmin3: false,
  address1: { value: '', },
  address2: { value: '', },
  firstName: { value: '', },
  idNumber: { value: '' },
  lastName: { value: '', },
  name: { value: '', },
  phone: { value: '', },
  zipCode: { value: '', },
};

export default class AddressModal extends Component<Props, State> {
  formValidator: FormValidator;
  phoneCleave: Cleave;
  state = initialState;
  initialized = false;

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      country: { required: 'required' },
      name: { required: 'required' },
      firstName: { required: 'required' },
      idNumber: { required: 'required' },
      lastName: { required: 'required' },
      phone: { required: 'required' },
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    const { address, isPosting, isPutting, cleanModalState } = nextProps;
    if (cleanModalState) {
      return this.setState(initialState);
    }
    if (address && !(isPosting || isPutting) /*&& !this.initialized*/) {
      this.setState(
        {
          country: { value: address.country.code },
          address1: { value: address.address1 },
          address2: { value: address.address2 },
          firstName: { value: address.firstName },
          idNumber: { value: address.idNumber },
          lastName: { value: address.lastName },
          name: { value: address.name },
          phone: { value: address.phone },
          zipCode: { value: address.zipCode },
          isLoadingAdmin1: true,
          isLoadingAdmin2: true,
          isLoadingAdmin3: true,
        });
      searchAdmin1Api(undefined, {
        route: endpoints.SEARCH_ADMIN1.replace(':country', address.country.code),
      }).toPromise().then((admin1Items: Admin[]) => {
        const location: Admin = admin1Items.find((admin: Admin) => address.admin1.id === admin.id);
        return this.setState({
          admin1Items,
          isLoadingAdmin1: false,
          admin1: {
            label: location.name,
            value: location.id,
          },
        });
      });
      searchAdmin2Api(undefined, {
        route: endpoints.SEARCH_ADMIN2.replace(':country/:admin1', `${address.country.code}/${address.admin1.id}`),
      }).toPromise().then((admin2Items: Admin[]) => {
        const location = admin2Items.find((admin: Admin) => address.admin2.id === admin.id);
        if (location) {
          this.setState({
            admin2Items,
            isLoadingAdmin2: false,
            admin2: {
              label: location.name,
              value: location.id,
            },
          });
        }
      });
      if (address.admin3) {
        searchAdmin3Api(undefined, {
          route: endpoints.SEARCH_ADMIN3
            .replace(':country/:admin1/:admin2', `${address.country.code}/${address.admin1.id}/${address.admin2.id}`),
        }).toPromise().then((admin3Items: Admin[]) => {
          const location = admin3Items.find((admin: Admin) => address.admin3.id === admin.id);
          if (location) {
            this.setState({
              admin3Items,
              isLoadingAdmin3: false,
              admin3: {
                label: location.name,
                value: location.id,
              },
            });
          }
        });
      }
      this.initialized = true;
    }
  }

  handleChangeCountry(option: Option) {
    this.setState({
      country: {
        value: option ? option.value : undefined,
      },
      admin1: {
        value: '',
      },
      admin1Items: undefined,
      isLoadingAdmin1: true,
      admin2: {
        value: '',
      },
      admin2Items: undefined,
      admin3: {
        value: '',
      },
      admin3Items: undefined,
    });
    if (option && option.value) {
      searchAdmin1Api(undefined, {
        route: endpoints.SEARCH_ADMIN1.replace(':country', `${option.value}`),
      }).toPromise().then((admin1Items: Admin[]) =>
        this.setState({ admin1Items, isLoadingAdmin1: false }));
    }
  }

  handleChangeAdmin1(option: Option) {
    const { country } = this.state;
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      admin1: {
        value: `${option.value}`,
        label: option.label,
      },
      admin2: {
        value: '',
      },
      admin2Items: undefined,
      isLoadingAdmin2: true,
      admin3: {
        value: '',
      },
      admin3Items: undefined,
    });
    searchAdmin2Api(undefined, {
      route: endpoints.SEARCH_ADMIN2.replace(':country/:admin1', `${country.value}/${option.value}`),
    }).toPromise().then((admin2Items: Admin[]) =>
      this.setState({ admin2Items, isLoadingAdmin2: false }));

  }

  handleChangeAdmin2(option: Option) {
    const { country, admin1 } = this.state;
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      admin2: {
        value: `${option.value}`,
        label: option.label,
      },
      isLoadingAdmin3: true,
      admin3: {
        value: '',
      },
      admin3Items: undefined,
    });
    if (country.value !== 'US') {
      searchAdmin3Api(undefined, {
        route: endpoints.SEARCH_ADMIN3
          .replace(':country/:admin1/:admin2', `${country.value}/${admin1.value}/${option.value}`),
      }).toPromise().then((admin3Items: Admin[]) =>
        this.setState({ admin3Items, isLoadingAdmin3: false })
      );
    }
  }

  handleChangeAdmin3(option: Option) {
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      admin3: {
        value: `${option.value}`,
        label: option.label,
      },
    });
  }

  handleChange(e: SyntheticInputEvent<HTMLInputElement>) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleChangePhone(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { value, rawValue } = e.target;
      if (value.length <= 15) {
        this.setState({ ['phone']: { value: rawValue } });
        this.phoneCleave.setRawValue(rawValue);
      }
      if (rawValue.length > 15) {
        this.phoneCleave.setRawValue(this.state.phone.value);
      }
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      if (name === 'phone') {
        if (value.length <= 15) {
          this.setState({ [name]: { value: rawValue } });
          this.phoneCleave.setRawValue(rawValue);
        }
        if (rawValue.length > 15) {
          this.phoneCleave.setRawValue(this.state.phone.value);
        }
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { postAddress, address, editAddress } = this.props;
    const {
      country, admin1, admin2, admin3, address1, address2, firstName, idNumber, lastName, name, phone, zipCode
    } = this.state;
    const errors = country.value ?
      getAddressFormValidator(country.value, 'modal').validateForm({
        country: country.value,
        admin1: admin1.value,
        admin2: admin2.value,
        admin3: admin3.value,
        address1: address1.value,
        firstName: firstName.value,
        idNumber: idNumber.value,
        lastName: lastName.value,
        name: name.value,
        phone: phone.value,
        // zipCode: zipCode.value,
      }) :
      this.formValidator.validateForm({
        country: country.value,
        name: name.value,
        firstName: firstName.value,
        idNumber: idNumber.value,
        lastName: lastName.value,
        phone: phone.value,
      });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else if (admin1.label && admin2.label) {
      const requestAddress: AddressRequest = {
        country: country.value,
        admin1: parseInt(admin1.value),
        admin2: parseInt(admin2.value),
        address1: address1.value,
        firstName: firstName.value,
        lastName: lastName.value,
        name: name.value,
        phone: phone.value,
      };

      if (country.value !== 'US' && admin3.label) {
        requestAddress.admin3 = parseInt(admin3.value, 10);
        requestAddress.idNumber = idNumber.value;
      }

      if (address2.value) {
        requestAddress.address2 = address2.value;
      }

      if (zipCode.value !== '') {
        requestAddress.zipCode = zipCode.value;
      }

      if (address) {
        if (!address2.value && address.address2) {
          requestAddress.address2 = null;
        }
        if (!zipCode.value && address.zipCode) {
          requestAddress.zipCode = null;
        }
        editAddress(requestAddress, address.id);
      } else {
        postAddress(requestAddress);
      }
    }
  }

  handleCancel(e?: Event) {
    if (e) e.preventDefault();
    this.props.handleClose();
  }

  handlePhoneInit(cleave: any) {
    this.phoneCleave = cleave;
  }

  render() {
    const { addressModalVisible, isPutting, isPosting, address, localValues } = this.props;
    const {
      country, admin1, admin2, admin3, admin1Items, admin2Items, admin3Items, isLoadingAdmin1,
      isLoadingAdmin2, isLoadingAdmin3, address1, address2, firstName, idNumber, lastName, name, phone, zipCode,
    } = this.state;
    return (
      <Modal open={addressModalVisible}
             handleClose={this.handleCancel.bind(this)}
             width={770}
             height={480}>
        <section className={commonStyle.modal}>
          <FormattedMessage id={address ? messages.title.edit.id : messages.title.add.id}>{
            txt => <h2>{txt}</h2>
          }</FormattedMessage>
          <AddressForm type="modal"
                       handleChangeCountry={this.handleChangeCountry.bind(this)}
                       handleChangeAdmin1={this.handleChangeAdmin1.bind(this)}
                       handleChangeAdmin2={this.handleChangeAdmin2.bind(this)}
                       handleChangeAdmin3={this.handleChangeAdmin3.bind(this)}
                       handleChange={this.handleChange.bind(this)}
                       handleChangePhone={this.handleChangePhone.bind(this)}
                       handleFocus={this.handleFocus.bind(this)}
                       isPutting={isPutting}
                       isPosting={isPosting}
                       country={country}
                       admin1={admin1}
                       admin2={admin2}
                       admin3={admin3}
                       admin1Items={admin1Items}
                       admin2Items={admin2Items}
                       admin3Items={admin3Items}
                       isLoadingAdmin1={isLoadingAdmin1}
                       isLoadingAdmin2={isLoadingAdmin2}
                       isLoadingAdmin3={isLoadingAdmin3}
                       address1={address1}
                       address2={address2}
                       firstName={firstName}
                       idNumber={idNumber}
                       lastName={lastName}
                       name={name}
                       phone={phone}
                       zipCode={zipCode}
                       handlePhoneInit={this.handlePhoneInit.bind(this)}
                       localValues={localValues}/>
          <section className={classNames(commonStyle.buttons, commonStyle.buttonsRow)}>
            <FormattedMessage id={messages.button.cancel.id}>{
              txt => <button type="button"
                             disabled={isPosting || isPutting}
                             className={classNames(buttonStyle.btn, buttonStyle.darkGrey)}
                             onClick={this.handleCancel.bind(this)}>
                {txt}</button>
            }</FormattedMessage>
            <FormattedMessage id={address ? messages.button.edit.id : messages.button.add.id}>{
              txt => <button type="button"
                             disabled={isPosting || isPutting}
                             className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             onClick={this.handleSubmit.bind(this)}>
                {txt}</button>
            }</FormattedMessage>
          </section>
        </section>
      </Modal>
    );
  }
}

AddressModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  postAddress: PropTypes.func.isRequired,
  editAddress: PropTypes.func.isRequired,
  address: PropTypes.object,
  isPosting: PropTypes.bool,
  isPutting: PropTypes.bool,
  addressModalVisible: PropTypes.bool.isRequired,
  cleanModalState: PropTypes.bool,
  localValues: PropTypes.object,
};

AddressModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/saved-addresses/components/AddressModal/AddressModal.jsx